import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Sparkles, Copy, CheckCircle2, XCircle, ArrowLeft, PenTool, ShieldCheck, Link2, Globe, Lock, Timer, Crown, Rocket, Zap, Clock, Heart, Award } from "lucide-react";
import FoundingBadge from "../components/FoundingBadge";
import { ReputationBadge } from "../components/ReputationBadge";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";

function fmtTraffic(n) {
  if (n == null) return "—";
  if (n >= 1000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K`;
  return `${n}`;
}

function expiryLabel(expiresAt) {
  if (!expiresAt) return "In progress";
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return "Expiring soon";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h >= 1 ? `Expires in ${h}h` : `Expires in ${m}m`;
}

function StatusPill({ label, state = "none", okText = "Verified", pendingText = "Not verified yet" }) {
  const verified = state === "verified";
  const pending = state === "pending";
  const rejected = state === "rejected";
  const cls = verified ? "border-sage/40 bg-sage/10"
    : pending ? "border-amber-500/40 bg-amber-500/10"
    : "border-stone-200 bg-white";
  return (
    <div className={`rounded-xl border px-4 py-3 ${cls}`} data-testid={`status-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="font-mono text-[10px] tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm font-medium flex items-center gap-1.5">
        {verified ? (<><CheckCircle2 size={15} className="text-sage-dark" /> {okText}</>)
          : pending ? (<><Clock size={15} className="text-amber-500" /> Awaiting verification</>)
          : rejected ? (<><XCircle size={15} className="text-slate-pass" /> Rejected — resubmit</>)
          : (<><XCircle size={15} className="text-slate-pass" /> {pendingText}</>)}
      </div>
    </div>
  );
}

export default function MatchDetails() {
  const { matchId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [gen, setGen] = useState(false);
  const [usage, setUsage] = useState(null);
  const [initiating, setInitiating] = useState(false);
  const [showPledge, setShowPledge] = useState(false);

  const loadUsage = async () => {
    try { const { data } = await api.get("/usage"); setUsage(data); } catch {}
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/matches/${matchId}`);
      setM(data);
    } catch {
      toast.error("Match not found");
      nav("/matches");
    }
  };
  useEffect(() => { load(); loadUsage(); /* eslint-disable-next-line */ }, [matchId]);

  const initiate = async () => {
    setShowPledge(false);
    setInitiating(true);
    try {
      const { data } = await api.post(`/matches/${matchId}/initiate`);
      setM(data);
      toast.success("Swap started — post & verify your backlink within 48h.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setInitiating(false);
    }
  };

  const generate = async () => {
    setGen(true);
    toast.info("GPT-4o is writing your guest post…");
    try {
      const { data } = await api.post(`/matches/${matchId}/generate`);
      setM(data);
      await loadUsage();
      toast.success("Your bridge article is ready");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setGen(false);
    }
  };

  if (!m) return <div className="max-w-4xl mx-auto px-6 py-12 font-mono text-sm text-muted-foreground animate-pulse">loading…</div>;

  const articles = m.articles;
  // Only the article the CURRENT user must publish (owner === me). It links to the partner.
  const myArticle = articles
    ? (articles.for_a.owner === user?.user_id ? articles.for_a : articles.for_b)
    : null;
  const myVerification = (m.verification || {})[user?.user_id];
  const myState = myVerification?.verified ? "verified"
    : myVerification?.rejected ? "rejected"
    : myVerification?.pending ? "pending" : "none";

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <button onClick={() => nav("/matches")} data-testid="back-to-matches"
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-ink transition-colors">
        <ArrowLeft size={16} /> All matches
      </button>

      {/* Match header */}
      <div className="mt-6 rounded-2xl border border-stone-200 bg-white overflow-hidden shadow-soft">
        <div className="grid sm:grid-cols-2">
          <div className="p-6 border-b sm:border-b-0 sm:border-r border-stone-200">
            <div className="font-mono text-[10px] tracking-widest text-muted-foreground">YOUR SITE</div>
            <div className="font-serif font-semibold text-xl mt-2">{m.me.url}</div>
            <div className="font-mono text-[11px] text-muted-foreground mt-1">{m.me.niche} · DA {m.me.da} · {fmtTraffic(m.me.traffic)}/mo</div>
          </div>
          <div className="p-6 relative">
            <div className="absolute top-6 right-6 flex flex-col items-end gap-1.5">
              <span className="bg-sage text-white px-3 py-1 rounded-full text-[11px] font-medium">MATCHED</span>
              {m.partner.high_traffic && <span className="bg-ink text-white px-2.5 py-1 rounded-full text-[10px] font-mono flex items-center gap-1"><Crown size={11} /> 10K+</span>}
            </div>
            <div className="font-mono text-[10px] tracking-widest text-terracotta">PARTNER</div>
            <div className="flex items-center gap-2 mt-2">
              <div className="font-serif font-semibold text-xl">{m.partner.name}</div>
              {m.partner.founding_member && <FoundingBadge size={15} />}
              {m.partner.reputation && <ReputationBadge rep={m.partner.reputation} />}
            </div>
            <div className="flex items-center gap-1.5 text-muted-foreground mt-1">
              {m.partner.url
                ? (<><Globe size={12} /><span className="font-mono text-[11px]">{m.partner.url}</span></>)
                : (<><Lock size={12} /><span className="font-mono text-[11px]">URL hidden until you verify</span></>)}
            </div>
            <div className="font-mono text-[11px] text-muted-foreground mt-0.5">{m.partner.niche} · DA {m.partner.da} · {fmtTraffic(m.partner.traffic)}/mo</div>
          </div>
        </div>
      </div>

      {/* Trade status + 48h expiry */}
      <div className="mt-4 grid sm:grid-cols-3 gap-3">
        <StatusPill label="YOUR POST" state={myState} okText="Verified" pendingText="Not verified yet" />
        <StatusPill label="PARTNER'S POST" state={m.partner_verified ? "verified" : "none"} okText="Verified" pendingText="Awaiting partner" />
        <div className={`rounded-xl border px-4 py-3 ${m.both_verified ? "border-sage/40 bg-sage/10" : "border-stone-200 bg-white"}`}>
          <div className="font-mono text-[10px] tracking-widest text-muted-foreground">TRADE</div>
          <div className="mt-1 text-sm font-medium flex items-center gap-1.5">
            {m.both_verified
              ? (<><CheckCircle2 size={15} className="text-sage-dark" /> Complete</>)
              : !m.me_initiated
              ? (<><Timer size={15} className="text-muted-foreground" /> {m.status === "active" ? expiryLabel(m.expires_at) : "In pipeline"}</>)
              : (<><Timer size={15} className="text-terracotta" /> {expiryLabel(m.expires_at)}</>)}
          </div>
        </div>
      </div>

      {m.me_initiated && !m.both_verified && (
        <div className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] px-4 py-3 text-[13px] text-amber-700 leading-relaxed" data-testid="swap-policy-note">
          <span className="font-medium">Complete within 48 hours.</span> Once a swap is agreed, both partners
          publish and verify their backlink within 48h. If an agreed swap expires without your link live, it counts
          as a missed swap and lowers your reputation — after 3 missed swaps, your account and domain are removed
          and blocked from future use. We'll email you reminders 24h and 12h before it expires.
        </div>
      )}

      {/* Initiate CTA — swap is in the pipeline until the user starts it */}
      {!m.me_initiated && (
        <div className={`mt-8 rounded-2xl border p-8 shadow-soft ${m.partner_initiated ? "border-terracotta/50 bg-terracotta-light/20" : "border-stone-200 bg-white"}`} data-testid="initiate-section">
          {m.partner_initiated && (
            <div className="mb-4 flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-[13px] text-terracotta-dark" data-testid="partner-initiated-banner">
              <Zap size={16} className="text-terracotta shrink-0" />
              <span className="font-medium">Your match already started this swap.</span>
              <span>Accept quickly — the 48h window is ticking{m.expires_at ? ` (${expiryLabel(m.expires_at).replace("Expires in ", "")} left)` : ""}.</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Rocket size={20} className="text-terracotta" />
            <h2 className="font-serif font-bold text-3xl tracking-tight">
              {m.partner_initiated ? "Accept this swap" : "Start the link share"}
            </h2>
          </div>
          <p className="text-muted-foreground text-sm mt-2 max-w-lg">
            This match is in your <span className="text-ink font-medium">pipeline</span>. Starting it uses one of your
            <span className="text-ink font-medium"> 3 active swap slots</span> and begins a
            <span className="text-ink font-medium"> 48-hour</span> window to generate your article, publish it, and verify the backlink.
            Until then, both sites stay safely matched with nothing to do.
          </p>
          <Button onClick={() => setShowPledge(true)} disabled={initiating} data-testid="initiate-swap-button"
            className={`mt-6 rounded-full h-11 px-6 text-white active:scale-95 transition-transform disabled:opacity-50 ${m.partner_initiated ? "bg-terracotta hover:bg-terracotta-dark" : "bg-ink hover:bg-ink/90"}`}>
            <Rocket size={16} className="mr-2" />
            {initiating ? "Starting…" : m.partner_initiated ? "Accept & start swap" : "Start swap (48h window)"}
          </Button>
        </div>
      )}

      <FairPlayPledge
        open={showPledge}
        onOpenChange={setShowPledge}
        accept={!!m.partner_initiated}
        reputation={user?.reputation}
        strikes={user?.strikes || 0}
        onConfirm={initiate}
        busy={initiating}
      />

      {m.me_initiated && (<>
      {/* AI Bridge Studio */}
      <div className="mt-8">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <PenTool size={20} className="text-terracotta" />
            <h2 className="font-serif font-bold text-3xl tracking-tight">AI Bridge Studio</h2>
          </div>
          {usage && (
            <div data-testid="ai-usage-counter"
              className={`rounded-full border px-4 py-1.5 text-sm font-mono ${usage.remaining <= 0 ? "border-terracotta/50 bg-terracotta-light/30 text-terracotta-dark" : "border-stone-200 bg-white text-muted-foreground"}`}>
              Monthly AI Articles remaining: <span className="font-bold text-ink">{usage.remaining}</span> / {usage.limit}
            </div>
          )}
        </div>

        {!articles && (
          <div className="mt-6 rounded-2xl border border-stone-200 bg-white p-10 text-center shadow-soft">
            <div className="h-14 w-14 rounded-full bg-terracotta-light/40 flex items-center justify-center mx-auto"><Sparkles size={26} className="text-terracotta" /></div>
            <h3 className="font-serif font-semibold text-2xl mt-5">Generate your guest post</h3>
            <p className="text-muted-foreground text-sm mt-2 max-w-md mx-auto">
              GPT-4o writes a 1,000-word SEO guest post that bridges
              <span className="text-ink font-medium"> {m.me.niche}</span> and
              <span className="text-ink font-medium"> {m.partner.niche}</span>, with a backlink to
              <span className="font-mono text-ink"> {m.partner.url || "your match's site"}</span> in the first 300 words.
            </p>
            <Button onClick={generate} disabled={gen || (usage && usage.remaining <= 0)} data-testid="generate-articles-button"
              className="mt-6 rounded-full bg-terracotta text-white hover:bg-terracotta-dark h-11 px-6 active:scale-95 transition-transform disabled:opacity-50">
              <Sparkles size={16} className="mr-2" /> {gen ? "Writing your post…" : "Generate Bridge Article"}
            </Button>
            {usage && usage.remaining <= 0 && (
              <p className="text-xs text-terracotta-dark mt-3 font-mono" data-testid="ai-limit-reached">
                You've used all {usage.limit} AI articles this month. Your allowance resets on the 1st.
              </p>
            )}
          </div>
        )}

        {articles && myArticle && (
          <>
            <div className="mt-6 rounded-xl border border-stone-200 bg-white p-4 flex items-center gap-3 flex-wrap">
              <span className="font-mono text-xs tracking-wide text-muted-foreground">BRIDGE TOPIC:</span>
              <span className="font-serif font-semibold" data-testid="bridge-topic">{articles.bridge_topic}</span>
            </div>
            <div className="mt-4 rounded-2xl border border-stone-200 bg-white overflow-hidden shadow-soft" data-testid="article-pane-mine">
              <div className="flex items-center justify-between border-b border-stone-200 p-4 bg-muted/50">
                <div className="min-w-0">
                  <div className="font-mono text-[10px] tracking-widest text-terracotta">YOUR POST — PUBLISH THIS ON {m.me.url}</div>
                  <div className="flex items-center gap-1.5 mt-1 text-muted-foreground text-[11px] font-mono">
                    <Link2 size={12} /> {myArticle.target_url ? `backlink to ${myArticle.target_url}` : "contains your match's backlink (URL hidden until verified)"}
                  </div>
                </div>
                <Button onClick={() => { navigator.clipboard.writeText(myArticle.html || ""); toast.success("HTML copied to clipboard"); }}
                  variant="outline" size="sm" data-testid="copy-html-mine" className="rounded-full border-stone-300 bg-white hover:bg-muted shrink-0">
                  <Copy size={14} className="mr-1.5" /> Copy HTML
                </Button>
              </div>
              <div className="article-html p-8 overflow-y-auto max-h-[640px]" dangerouslySetInnerHTML={{ __html: myArticle.html || "" }} />
            </div>
          </>
        )}
      </div>

      {/* Handoff & Verification */}
      {articles && (
        <VerifySection matchId={matchId} partnerUrl={m.partner.url} verification={myVerification} onDone={load} />
      )}
      </>)}
    </div>
  );
}

function VerifySection({ matchId, partnerUrl, verification, onDone }) {
  const [liveUrl, setLiveUrl] = useState(verification?.live_url || "");
  const [busy, setBusy] = useState(false);

  const verify = async () => {
    if (!liveUrl) { toast.error("Paste the live URL first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/matches/${matchId}/verify`, { live_url: liveUrl });
      if (data.verified) toast.success("Backlink verified — trade confirmed!");
      else if (data.pending_review) toast.info(data.message || "Submitted — your backlink is now awaiting verification.", { duration: 8000 });
      else toast.error(data.message || "We couldn't verify that link. Please try again.", { duration: 7000 });
      await onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const verified = verification?.verified;
  const pending = !verified && verification?.pending;
  const rejected = !verified && verification?.rejected;

  return (
    <div className="mt-8 rounded-2xl border border-stone-200 bg-white p-8 shadow-soft">
      <div className="flex items-center gap-2">
        <ShieldCheck size={20} className="text-terracotta" />
        <h2 className="font-serif font-bold text-3xl tracking-tight">Handoff & verification</h2>
      </div>
      <p className="text-muted-foreground text-sm mt-2">
        Once you've published your post, paste its live URL. We check the page to confirm your backlink is live —
        if we can't confirm it automatically, it's sent for a quick manual check.
        {partnerUrl
          ? (<> Your match's site: <span className="font-mono text-ink">{partnerUrl}</span>.</>)
          : (<> Your match's URL stays <span className="text-ink font-medium">hidden</span> until this check passes — then it's revealed here.</>)}
      </p>
      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <Input value={liveUrl} onChange={(e) => setLiveUrl(e.target.value)} data-testid="verify-url-input"
          placeholder="https://yoursite.com/your-guest-post" className="rounded-xl bg-bone border-stone-300 h-11 font-mono" />
        <Button onClick={verify} disabled={busy} data-testid="verify-link-button"
          className="rounded-full bg-ink text-white hover:bg-ink/90 h-11 px-6 shrink-0 active:scale-95 transition-transform">
          {busy ? "Checking…" : verified ? "Re-verify" : "Verify link"}
        </Button>
      </div>
      {verification && (
        verified ? (
          <div data-testid="verify-status" className="mt-4 flex items-center gap-2 text-sm font-medium text-sage-dark">
            <CheckCircle2 size={18} /> Verified · trade confirmed{verification.method === "manual" ? " (manually reviewed)" : ""}
          </div>
        ) : rejected ? (
          <div data-testid="verify-status" className="mt-4 rounded-xl border border-slate-pass/40 bg-slate-pass/5 px-4 py-3 text-sm text-terracotta-dark" data-testid-alt="verify-rejected">
            <div className="flex items-center gap-2 font-medium"><XCircle size={18} /> Not found — please fix the link and resubmit</div>
            <div className="mt-1 text-muted-foreground text-[13px]">{verification.reject_reason || "We couldn't find your backlink on that page."} Update your published post so it links to your match's site, then hit Verify again.</div>
          </div>
        ) : pending ? (
          <div data-testid="verify-status" className="mt-4 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm" data-testid-alt="verify-pending">
            <div className="flex items-center gap-2 font-medium text-amber-500"><Clock size={18} /> Awaiting verification</div>
            <div className="mt-1 text-muted-foreground text-[13px]">We couldn't confirm your backlink automatically, so it's queued for a quick check. Your dashboard will update to Verified as soon as it's confirmed — nothing more to do for now.</div>
          </div>
        ) : (
          <div data-testid="verify-status" className="mt-4 flex items-center gap-2 text-sm font-medium text-terracotta-dark">
            <XCircle size={18} /> Not verified yet
          </div>
        )
      )}
    </div>
  );
}


function FairPlayPledge({ open, onOpenChange, accept, reputation, strikes, onConfirm, busy }) {
  const chancesLeft = Math.max(0, 3 - (strikes || 0));
  const tier = reputation?.tier || "New";
  const reliability = reputation?.reliability;
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="rounded-2xl max-w-lg" data-testid="fair-play-pledge">
        <AlertDialogHeader>
          <div className="mx-auto h-12 w-12 rounded-2xl bg-terracotta-light/50 flex items-center justify-center mb-1">
            <Heart size={22} className="text-terracotta" fill="currentColor" />
          </div>
          <AlertDialogTitle className="font-serif text-2xl text-center tracking-tight">
            A friendly heads-up before you start 💛
          </AlertDialogTitle>
          <AlertDialogDescription className="text-center text-[13px]">
            You're about to agree to a real backlink swap. Here's how we keep blinxd fair and rewarding for everyone.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3 my-1">
          <PledgeRow Icon={Timer} tone="amber" title="Complete within 48 hours"
            body="Once you start, both partners publish and verify their backlink within 48h. We'll gently remind you 24h and 12h before it's due — so you're never caught out." />
          <PledgeRow Icon={Award} tone="sage" title="Every finished swap grows your reputation"
            body={`Delivering builds trust with future partners. You're currently a "${tier}" member${reliability != null ? ` at ${reliability}% reliability` : ""} — keep it up!`} />
          <PledgeRow Icon={ShieldCheck} tone="rose" title="Please only start swaps you'll finish"
            body={`If an agreed swap expires without your link live, it counts as a missed swap and dips your reputation. After 3 missed swaps an account and its domain are retired from blinxd. You have ${chancesLeft} of 3 chances remaining — plenty of room, and we hope you never use one.`} />
        </div>

        <p className="text-center text-[12px] text-muted-foreground px-2">
          Start with confidence — publish, verify, and you'll be building a stellar reputation in no time. 🌟
        </p>

        <AlertDialogFooter className="mt-2">
          <AlertDialogCancel data-testid="pledge-cancel" className="rounded-full">Not just yet</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} disabled={busy} data-testid="pledge-confirm"
            className="rounded-full bg-terracotta text-white hover:bg-terracotta-dark">
            <Rocket size={15} className="mr-1.5" />
            {accept ? "I'm in — accept & start" : "I'm in — start the swap"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function PledgeRow({ Icon, tone, title, body }) {
  const tones = {
    amber: "border-amber-500/25 bg-amber-500/[0.06] text-amber-600",
    sage: "border-sage/30 bg-sage/10 text-sage-dark",
    rose: "border-rose-400/25 bg-rose-400/[0.07] text-rose-500",
  };
  return (
    <div className="flex items-start gap-3 rounded-xl border border-stone-200 bg-white px-3.5 py-3">
      <div className={`h-8 w-8 shrink-0 rounded-lg border flex items-center justify-center ${tones[tone]}`}>
        <Icon size={16} />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-medium text-ink">{title}</div>
        <p className="text-[12.5px] text-muted-foreground mt-0.5 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
